"use client";
import { useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { X, AlertCircle, Lightbulb, MessageCircle, Paperclip, Send, Loader2, Check } from "lucide-react";
import MicButton from "@/components/MicButton";
import { api } from "@/lib/api";

type Tab = "problem" | "feature" | "comment";

const TABS: { id: Tab; label: string; icon: React.ReactNode; description: string; placeholder: string; submitLabel: string }[] = [
  {
    id: "problem",
    label: "Problem",
    icon: <AlertCircle className="w-4 h-4" />,
    description: "What did you do, what happened, and what did you expect?",
    placeholder: "Steps: clicked 'Next' after writing the brief.\nExpected: moves to Creator Discovery.\nActual: page stayed on the brief step.",
    submitLabel: "Report bug",
  },
  {
    id: "feature",
    label: "Feature request",
    icon: <Lightbulb className="w-4 h-4" />,
    description: "Describe the feature you'd like and why it would help.",
    placeholder: "I'd love to be able to filter creators by engagement rate range...",
    submitLabel: "Send request",
  },
  {
    id: "comment",
    label: "General",
    icon: <MessageCircle className="w-4 h-4" />,
    description: "Share any thoughts, observations, or general feedback.",
    placeholder: "The outreach emails feel really natural - nice work!",
    submitLabel: "Send comment",
  },
];

interface Props {
  onClose: () => void;
}

export default function FeedbackPopup({ onClose }: Props) {
  const pathname = usePathname();
  const [tab, setTab] = useState<Tab>("problem");
  const [text, setText] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [video, setVideo] = useState<File | null>(null);
  const [state, setState] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_SCREENSHOTS = 5;

  const campaignMatch = pathname?.match(/^\/campaigns\/([^/]+)/);
  const campaignId = campaignMatch ? campaignMatch[1] : undefined;

  const activeTab = TABS.find(t => t.id === tab)!;

  const reset = () => { setText(""); setScreenshots([]); setVideo(null); setState("idle"); setError(""); };

  const handleClose = () => { if (state === "done") reset(); onClose(); };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setError("");

    const images = files.filter(f => f.type.startsWith("image/"));
    const videos = files.filter(f => f.type.startsWith("video/"));

    if (images.length) {
      setScreenshots(prev => {
        const next = [...prev, ...images];
        if (next.length > MAX_SCREENSHOTS) setError(`You can attach up to ${MAX_SCREENSHOTS} screenshots.`);
        return next.slice(0, MAX_SCREENSHOTS);
      });
    }
    if (videos.length) {
      setVideo(videos[0]);
      if (videos.length > 1) setError("Only one video can be attached.");
    }
  };

  const removeScreenshot = (idx: number) => setScreenshots(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!text.trim()) return;
    setState("submitting");
    setError("");
    try {
      await api.feedback.submit({
        title: `[${activeTab.label}] ${text.trim().slice(0, 80)}`,
        text: text.trim(),
        feedbackType: tab,
        pageUrl: pathname || "",
        campaignId,
        screenshots,
        video,
      });
      setState("done");
    } catch (e: any) {
      setError(e.message || "Could not submit feedback");
      setState("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-lg font-semibold text-gray-900">Send feedback</h2>
          <button onClick={handleClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {state === "done" ? (
          <div className="px-6 pb-6 flex items-center gap-3 text-gray-700">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
            <p className="text-sm">Thanks - your feedback was sent!</p>
          </div>
        ) : (
          <div className="px-6 pb-6 space-y-4">
            {/* Tabs */}
            <div className="flex items-center gap-2">
              {TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setText(""); setError(""); }}
                  className={`flex items-center gap-1.5 text-sm font-medium px-3.5 py-1.5 rounded-full border transition-all ${
                    tab === t.id
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700"
                  }`}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600">{activeTab.description}</p>

            {/* Textarea with voice input */}
            <div className="relative">
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={activeTab.placeholder}
                rows={6}
                className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 pr-10 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-300 resize-none text-gray-700 placeholder-gray-400 transition-colors"
              />
              <MicButton
                className="absolute right-2.5 bottom-4 text-[#8AB41D] hover:text-[#6E9216]"
                iconSize="w-5 h-5"
                onTranscript={t => setText(prev => (prev + " " + t).trimStart())}
              />
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}

            {tab !== "comment" ? (
              // Attachments + submit (Problem / Feature request)
              <div className="space-y-2.5">
                {/* Attachment chips */}
                {(screenshots.length > 0 || video) && (
                  <div className="flex flex-wrap gap-2">
                    {screenshots.map((f, i) => (
                      <span
                        key={`s-${i}`}
                        className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 pl-3 pr-1.5 py-1.5 rounded-full max-w-[220px]"
                      >
                        <Paperclip className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <button
                          onClick={() => removeScreenshot(i)}
                          aria-label="Remove screenshot"
                          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                    {video && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 pl-3 pr-1.5 py-1.5 rounded-full max-w-[220px]">
                        <Paperclip className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{video.name}</span>
                        <button
                          onClick={() => setVideo(null)}
                          aria-label="Remove video"
                          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={screenshots.length >= MAX_SCREENSHOTS && !!video}
                      className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Paperclip className="w-4 h-4" />
                      {screenshots.length > 0 || video ? "Add more" : "Add screenshots or video"}
                    </button>
                    <span className="text-[11px] text-gray-400">
                      {screenshots.length}/{MAX_SCREENSHOTS} screenshots{video ? " · 1 video" : ""}
                    </span>
                  </div>
                  <button
                    onClick={submit}
                    disabled={!text.trim() || state === "submitting"}
                    className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all ${
                      text.trim()
                        ? "bg-[#D5E83F] text-[#1A1814] hover:opacity-90 active:scale-95 shadow-sm"
                        : "text-gray-400 disabled:cursor-not-allowed"
                    }`}
                  >
                    {state === "submitting" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {activeTab.submitLabel}
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={onFilePicked} />
              </div>
            ) : (
              // Submit only, centered (General)
              <div className="flex justify-center">
                <button
                  onClick={submit}
                  disabled={!text.trim() || state === "submitting"}
                  className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all ${
                    text.trim()
                      ? "bg-[#D5E83F] text-[#1A1814] hover:opacity-90 active:scale-95 shadow-sm"
                      : "text-gray-400 disabled:cursor-not-allowed"
                  }`}
                >
                  {state === "submitting" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {activeTab.submitLabel}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
